import {infiniteQueryOptions} from '@tanstack/react-query'
import {notFound} from '@tanstack/react-router'
import {createServerFn} from '@tanstack/react-start'
import {z} from 'zod'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductDefaultAppearanceId,
    getProductDefaultViewId,
    getVirtualProductId,
    parseProductId,
} from '~/utils/product'
import fetchProducts from '~/client/spreadshop/old/fetchProducts'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import {applyServerFnCache} from '~/client/fn/utils'
import {Locale, Maybe} from '~/gql/graphql'

const CACHE_TIME_SEC = 60 * 60 * 24 // 24h

const productsParams = z
    .object({
        locale: z.nativeEnum(Locale).optional(),
        offset: z.number().min(0).optional(),
        departmentId: z.string().optional(),
        categoryId: z.string().optional(),
        appearanceId: z.string().optional(),
        sizeId: z.string().optional(),
        ideaId: z.string().optional(),
        collectionId: z.string().optional(),
    })
    .optional()

const getProducts = createServerFn({
    method: 'GET',
})
    .inputValidator((data: unknown) => {
        const result = productsParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        const {
            locale,
            offset,
            departmentId,
            categoryId,
            appearanceId,
            sizeId,
            ideaId,
            collectionId,
        } = data ?? {}

        const response = await fetchProducts(offset, locale, {
            query: categoryId ?? departmentId,
            colorId: appearanceId,
            sizeId: sizeId,
            ideaId: ideaId,
            collectionId: collectionId,
        })

        applyServerFnCache(CACHE_TIME_SEC)

        const products: Maybe<ProductWithIds[]> | undefined = response.products?.items?.map(
            (product) => {
                const {ideaId, sellableId} = parseProductId(product?.id!)

                const imageSrc = product?.image?.src
                const baseDesignImageSrc = getCompositionImageBaseUrl(imageSrc)!

                const defaultAppearanceId = getProductDefaultAppearanceId(imageSrc)
                const defaultViewId = getProductDefaultViewId(imageSrc) ?? '1'
                return {
                    ...product,
                    designImage: {
                        src: getDesignImageSrc(
                            baseDesignImageSrc,
                            defaultViewId,
                            defaultAppearanceId
                        ),
                        srcset: getDesignImageSrcSet(
                            baseDesignImageSrc,
                            defaultViewId,
                            defaultAppearanceId
                        ),
                        altText: product?.image?.altText,
                    },
                    defaultAppearanceId: defaultAppearanceId,
                    defaultViewId: defaultViewId,
                    vpId: getVirtualProductId(imageSrc),
                    ideaId,
                    sellableId,
                }
            }
        )

        return {
            ...response.products,
            items: products,
        }
    })

export const productsInfiniteQueryOptions = (
    departmentId?: string,
    categoryId?: string,
    appearanceId?: string,
    sizeId?: string,
    ideaId?: string,
    collectionId?: string,
    locale: Locale = Locale.EnEu
) =>
    infiniteQueryOptions({
        queryKey: [
            locale,
            'products',
            ideaId ? ideaId : 'without-idea',
            collectionId ? collectionId : 'without-collection',
            departmentId ?? '',
            categoryId ?? '',
            appearanceId ?? '',
            sizeId ?? '',
        ],
        queryFn: ({pageParam: offset}) =>
            getProducts({
                data: {
                    offset,
                    departmentId,
                    categoryId,
                    appearanceId,
                    sizeId,
                    ideaId,
                    collectionId,
                    locale,
                },
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const pageInfo = lastPage.pageInfo
            const totalCount = pageInfo?.totalCount ?? 0
            const offset = pageInfo?.offset ?? 0
            const limit = pageInfo?.limit ?? 0

            const hasMoreItems = totalCount > offset + limit
            return hasMoreItems ? offset + limit : undefined
        },
        getPreviousPageParam: (firstPage) => {
            const pageInfo = firstPage.pageInfo
            const offset = pageInfo?.offset ?? 0
            const limit = pageInfo?.limit ?? 0
            return Math.max(offset - limit, 0)
        },
        gcTime: 1000 * CACHE_TIME_SEC, // 24h
        staleTime: 1000 * CACHE_TIME_SEC, // 24h
        placeholderData: (previousData) => previousData,
        select: (data) => [...data.pages.flatMap((page) => page.items ?? [])],
    })
